import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import Button from '@appchoose/button';
import { Loader2 } from 'lucide-react';

type AnalysisV2ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLaunch: (prompt?: string) => void;
  loading: boolean;
};

type ModalStep = 'categories' | 'pipeline';

const PIPELINE_STEP_KEYS = [
  'components.analysis_v2_modal.step_1',
  'components.analysis_v2_modal.step_2',
  'components.analysis_v2_modal.step_3',
  'components.analysis_v2_modal.step_4',
] as const;

export function AnalysisV2Modal({
  open,
  onOpenChange,
  onLaunch,
  loading,
}: AnalysisV2ModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<ModalStep>('categories');
  const [prompt, setPrompt] = useState('');

  useEffect(() => {
    if (open) {
      setStep('categories');
      setPrompt('');
    }
  }, [open]);

  const handleLaunch = () => {
    const trimmed = prompt.trim();
    onLaunch(trimmed ? trimmed : undefined);
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !loading && onOpenChange(val)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('components.analysis_v2_modal.title')}</DialogTitle>
        </DialogHeader>

        {step === 'categories' ? (
          <div className="space-y-2 py-2">
            <label
              htmlFor="analysis-v2-categories"
              className="text-sm font-medium"
            >
              {t('components.analysis_v2_modal.categories_label')}
            </label>
            <Textarea
              id="analysis-v2-categories"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder={t(
                'components.analysis_v2_modal.categories_placeholder'
              )}
              className="min-h-[140px] resize-none"
            />
            <p className="text-xs text-muted-foreground">
              {t('components.analysis_v2_modal.categories_helper')}
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <p className="text-sm font-medium">
              {t('components.analysis_v2_modal.pipeline_title')}
            </p>
            <ol className="space-y-3">
              {PIPELINE_STEP_KEYS.map((stepKey, index) => (
                <li key={stepKey} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                    {index + 1}
                  </span>
                  <span className="text-sm">{t(stepKey)}</span>
                </li>
              ))}
            </ol>
            <div className="space-y-1 rounded-md bg-gray-50 p-3">
              <p className="text-sm font-medium">
                {t('components.analysis_v2_modal.estimated_time')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('components.analysis_v2_modal.slack_notice')}
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-3">
          {step === 'categories' ? (
            <>
              <Button
                type="button"
                appearance="outline"
                onClick={() => onOpenChange(false)}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type="button"
                appearance="primary"
                onClick={() => setStep('pipeline')}
              >
                {t('general.next')}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                appearance="outline"
                onClick={() => setStep('categories')}
                disabled={loading}
              >
                {t('general.previous')}
              </Button>
              <Button
                type="button"
                appearance="primary"
                onClick={handleLaunch}
                disabled={loading}
                className="flex items-center gap-2"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('components.analysis_v2_modal.launch')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
